/**
 * User Profile Service
 * Handles all API interactions with the backend User Profile endpoints.
 * Provides methods for managing user search history, preferences, and profile data.
 * 
 * @module userProfileService
 */

import api from '../utils/apiv2';

/**
 * User Profile Service class
 * Manages user profile data and search history through backend API
 */
class UserProfileService {
  /**
   * Clean filters by only keeping valid sorting and filtering keys
   * @private
   * @param {Object} filters - Raw filters object
   * @returns {Object} Cleaned filters object with only valid keys
   */
  /**
   * Clean filters by removing preference-related keys that shouldn't be persisted
   * @private
   * @param {Object} filters - Raw filters object
   * @returns {Object} Cleaned filters object
   */
  _cleanFilters(filters) {
    if (!filters || typeof filters !== 'object') return {};
    
    // Create a copy and remove preference keys
    const cleanedFilters = { ...filters };
    delete cleanedFilters.lang;
    delete cleanedFilters.cur;
    
    return cleanedFilters;
  }

  /**
   * Get recent searches for a user
   * @param {string} userId - User ID from JWT token
   * @returns {Promise<Array>} Array of recent search objects, empty array on error
   */
  async getRecentSearches(userId) {
    try {
      if (!userId) {
        console.warn('UserProfileService: No userId provided for getRecentSearches');
        return [];
      }

      const response = await api.get(`/profile/user/${userId}/recent-searches`);
      const recentSearches = response.data.recentSearches || [];
      
      // Clean filters from existing searches to remove any contaminated data
      return recentSearches.map(search => ({
        ...search,
        filters: this._cleanFilters(search.filters)
      }));
    } catch (error) {
      console.error('UserProfileService: Failed to fetch recent searches:', error);
      // Always return empty array on error to prevent UI breaking
      return [];
    }
  }

  /**
   * Save a new search to user's recent searches
   * @param {string} userId - User ID from JWT token
   * @param {Object} searchData - Search data to save
   * @param {string} searchData.searchKey - Unique search identifier
   * @param {Object} searchData.params - Search parameters (dates, locations, age)
   * @param {Array} [searchData.savedOffers] - Array of saved offer IDs
   * @param {Object} [searchData.filters] - UI state/filters to preserve
   * @returns {Promise<Array|null>} Updated searches array or null on error
   */
  async saveRecentSearch(userId, searchData, lastCampaign = null) {
    try {
      if (!userId) {
        console.warn('UserProfileService: No userId provided for saveRecentSearch');
        return null;
      }

      if (!searchData.searchKey || !searchData.params) {
        console.warn('UserProfileService: Missing required searchKey or params');
        return null;
      }

      const payload = {
        searchData: {
          searchKey: searchData.searchKey,
          params: {
            endDate: searchData.params.endDate,
            startDate: searchData.params.startDate,
            fromLocationCode: searchData.params.fromLocationCode,
            toLocationCode: searchData.params.toLocationCode,
            age: searchData.params.age,
            residency: searchData.params.residency,
            cur: searchData.params.cur
          },
          savedOffers: searchData.savedOffers || [],
          filters: this._cleanFilters(searchData.filters || {})
        },
        ...(lastCampaign && { lastCampaign })
      };

      const response = await api.post(`/profile/user/${userId}/recent-search`, payload);
      return {
        recentSearches: response.data.recentSearches || null,
        lastCampaign: response.data.lastCampaign || null
      };
    } catch (error) {
      console.error('UserProfileService: Failed to save recent search:', error);
      return null;
    }
  }

  /**
   * Save multiple searches at once (batch operation)
   * @param {string} userId - User ID from JWT token
   * @param {Array} searchesArray - Array of search data objects
   * @returns {Promise<Array|null>} Updated searches array or null on error
   */
  async saveMultipleSearches(userId, searchesArray) {
    try {
      if (!userId) {
        console.warn('UserProfileService: No userId provided for saveMultipleSearches');
        return null;
      }

      if (!Array.isArray(searchesArray) || searchesArray.length === 0) {
        console.warn('UserProfileService: Invalid searches array provided');
        return null;
      }

      // Validate and format each search
      const validSearches = searchesArray
        .filter(search => search.searchKey && search.params)
        .map(search => ({
          searchKey: search.searchKey,
          params: {
            endDate: search.params.endDate,
            startDate: search.params.startDate,
            fromLocationCode: search.params.fromLocationCode,
            toLocationCode: search.params.toLocationCode,
            age: search.params.age,
            residency: search.params.residency
          },
          savedOffers: search.savedOffers || [],
          filters: this._cleanFilters(search.filters || {}),
          timestamp: search.timestamp // Include timestamp if provided
        }));

      if (validSearches.length === 0) {
        console.warn('UserProfileService: No valid searches to save');
        return null;
      }

      const response = await api.post(`/profile/user/${userId}/recent-search`, {
        searchData: validSearches,
      });
      return response.data.recentSearches || null;
    } catch (error) {
      console.error('UserProfileService: Failed to save multiple searches:', error);
      return null;
    }
  }

  /**
   * Get user profile data
   * @param {string} userId - User ID from JWT token
   * @returns {Promise<Object|null>} User profile data or null on error
   */
  async getUserProfile(userId) {
    try {
      if (!userId) {
        console.warn('UserProfileService: No userId provided for getUserProfile');
        return null;
      }

      const response = await api.get(`/profile/user/${userId}`);
      return response.data || null;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.log('UserProfileService: User profile not found, will be created on first update');
        return null;
      }
      console.error('UserProfileService: Failed to get user profile:', error);
      return null;
    }
  }

  /**
   * Update user profile data
   * @param {string} userId - User ID from JWT token
   * @param {Object} profileData - Profile data to update
   * @returns {Promise<Object|null>} Updated profile data or null on error
   */
  async updateUserProfile(userId, profileData) {
    try {
      if (!userId) {
        console.warn('UserProfileService: No userId provided for updateUserProfile');
        return null;
      }

      if (!profileData || Object.keys(profileData).length === 0) {
        console.warn('UserProfileService: No profile data provided for update');
        return null;
      }

      const response = await api.put(`/user/${userId}`, profileData);
      return response.data || null;
    } catch (error) {
      console.error('UserProfileService: Failed to update user profile:', error);
      return null;
    }
  }

  /**
   * Update user activity data
   * @param {string} userId - User ID from JWT token
   * @param {Object} activityData - Activity data to update
   * @returns {Promise<Object|null>} Updated activity data or null on error
   */
  async updateUserActivity(userId, activityData) {
    try {
      if (!userId) {
        console.warn('UserProfileService: No userId provided for updateUserActivity');
        return null;
      }

      if (!activityData || Object.keys(activityData).length === 0) {
        console.warn('UserProfileService: No activity data provided for update');
        return null;
      }

      const response = await api.put(`/profile/user/${userId}/activity`, activityData);
      return response.data?.activity || response.data || null;
    } catch (error) {
      console.error('UserProfileService: Failed to update user activity:', error);
      return null;
    }
  }

  /**
   * Add a booking to price monitoring
   * @param {string} userId - User ID from JWT token
   * @param {string} bookingReference - Booking reference number
   * @param {string} searchKey - Search key to associate the booking with
   * @returns {Promise<Object|null>} Result object or null on error
   */
  async addMonitoredBooking(userId, bookingReference, searchKey) {
    try {
      if (!userId) {
        console.warn('UserProfileService: No userId provided for addMonitoredBooking');
        return null;
      }

      if (!bookingReference || !searchKey) {
        console.warn('UserProfileService: Missing required bookingReference or searchKey');
        return null;
      }

      const response = await api.post(`/profile/user/${userId}/price-monitoring`, {
        bookingReference,
        searchKey
      });

      return response.data || null;
    } catch (error) {
      console.error('UserProfileService: Failed to add monitored booking:', error);
      return null;
    }
  }

  /**
   * Migrate local search history to backend when user first logs in
   * @param {string} userId - User ID from JWT token
   * @param {Array} localSearches - Array of local searches to migrate
   * @returns {Promise<boolean>} Success status
   */
  async migrateLocalSearchHistory(userId, localSearches) {
    try {
      if (!userId || !Array.isArray(localSearches) || localSearches.length === 0) {
        return false;
      }

      // Transform local searches to backend format
      const transformedSearches = localSearches
        .filter(search => search.params) // Only migrate valid searches
        .map(search => ({
          searchKey: search.searchKey,
          params: search.params,
          savedOffers: search.savedOffers || [],
          filters: this._cleanFilters(search.filters || {}),
          timestamp: search.timestamp // Preserve original timestamp
        }));

      if (transformedSearches.length === 0) {
        return false;
      }

      const response = await this.saveMultipleSearches(userId, transformedSearches);
      return response !== null;
    } catch (error) {
      console.error('UserProfileService: Failed to migrate local search history:', error);
      return false;
    }
  }

  /**
   * Save a new search to anonymous user profile
   * @param {string} externalClientId - External client ID for anonymous user
   * @param {Object} searchData - Search data to save
   * @param {string} searchData.searchKey - Unique search identifier
   * @param {Object} searchData.params - Search parameters (dates, locations, age)
   * @param {Array} [searchData.savedOffers] - Array of saved offer IDs
   * @param {Object} [searchData.filters] - UI state/filters to preserve
   * @param {Object} [lastCampaign] - Campaign data { name, expiry }
   * @returns {Promise<boolean>} True if successfully saved, false otherwise
   */
  async saveAnonymousRecentSearch(externalClientId, searchData, lastCampaign = null) {
    try {
      if (!externalClientId) {
        console.warn('UserProfileService: No externalClientId provided for saveAnonymousRecentSearch');
        return false;
      }

      if (!searchData.searchKey || !searchData.params) {
        console.warn('UserProfileService: Missing required searchKey or params');
        return false;
      }

      const payload = {
        searchData: {
          searchKey: searchData.searchKey,
          params: {
            endDate: searchData.params.endDate,
            startDate: searchData.params.startDate,
            fromLocationCode: searchData.params.fromLocationCode,
            toLocationCode: searchData.params.toLocationCode,
            age: searchData.params.age,
            residency: searchData.params.residency,
            cur: searchData.params.cur
          },
          savedOffers: searchData.savedOffers || [],
          filters: this._cleanFilters(searchData.filters || {})
        },
        ...(lastCampaign && { lastCampaign })
      };

      const response = await api.post(`/profile/anonymous/${externalClientId}/recent-search`, payload);
      
      // Check if the response indicates success
      if (response.status === 200 && response.data?.success) {
        return true;
      }

      console.warn('UserProfileService: Unexpected response format for anonymous search:', response.data);
      return false;
    } catch (error) {
      console.error('UserProfileService: Failed to save anonymous recent search:', error);
      return false;
    }
  }
}

// Export singleton instance
const userProfileService = new UserProfileService();

// Also export the class for testing
export { UserProfileService };
export default userProfileService;
